<!-- <template>
    <div style="background: #232741; color: #5af78e; padding: 15px; border-radius: 8px; font-family: monospace;">
      <h3>Debug: Selected Units Array ({{ selectionData.selectedUnits.length }})</h3>
      <pre>{{ selectionData.selectedUnits }}</pre>
    </div>
</template> -->

<template>
    <div class="page-container">
      <div class="card-wrapper">
        
        <!-- Top Decorative Activation Icon Badge -->
        <div class="header-icon-container">
          <div class="badge-glow-ring">
            <svg viewBox="0 0 24 24" class="svg-shield">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="M9 11l2 2 4-4" class="check-path" />
            </svg>
          </div>
        </div>
  
        <!-- Main Section Typography Titles -->
        <header class="summary-header">
          <h1 class="main-title">Ready to Activate</h1>
          <p class="subtitle">Review your property activation details before finalizing.</p>
        </header>
  
        <!-- Blue High-Level Core Metrics Banner Overview Box -->
        <section class="activation-banner">
          <div class="banner-top-row">
            <div class="label-group">
              <span class="meta-label">ACTIVATION TYPE</span>
              <h2 class="banner-highlight-title">Guaranteed Rent</h2>
            </div>
            <!-- Absolute Layer Decorative Shield Wireframe Icon -->
            <svg viewBox="0 0 24 24" class="banner-wireframe-shield">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          
          <div class="banner-bottom-row">
            <div class="metric-block">
              <span class="meta-label">SELECTED PROPERTY</span>
              <span class="value-text">
                {{ incomingData.totalPropertiesSelected }} ({{ incomingData.totalUnitsSelected }} units)
              </span>
            </div>
            <div class="metric-block align-right">
              <span class="meta-label">EST. REVENUE</span>
              <span class="value-text price-text">
                &#8358;{{ formatPrice(totalEstimatedRevenue) }}/yr
              </span>
            </div>
          </div>
        </section>
  
        <!-- Selected Child Listings Stream Section -->
        <section class="details-section">
          <h3 class="section-label">Selected Units</h3>
  
          <div class="units-timeline-stack">
            <div 
              v-for="(unit, index) in incomingData.selectedUnits" 
              :key="unit.unitId" 
              class="unit-card-wrapper"
            >
              <!-- Card Body Component Base Grid Layer -->
              <div class="unit-summary-card">
                <div class="icon-avatar-box">
                  <svg viewBox="0 0 24 24" class="svg-building">
                    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                  </svg>
                </div>
                
                <div class="unit-text-metrics">
                  <h4 class="unit-title">{{ unit.unitName }} - {{ unit.unitType }}</h4>
                  <p class="unit-date-label">Rent Date: {{ unit.rentDate || '05 May, 2026' }}</p>
                  <span class="unit-card-price">&#8358;{{ formatPrice(unit.unitPrice) }}/yr</span>
                </div>
              </div>

            </div>
          </div>
        </section>
  
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'PropertySummary',
    props: {
      /**
       * Accepts the compiled selection data bundle generated in previous layout states.
       * Includes a fully integrated fallback mock layout mirroring the current image visual.
       */
      selectionData: {
        type: Object,
        required: false,
        default: null
      }
    },
    computed: {
      // If no props are mapped, hooks the mock array matching the reference design layout perfectly
      incomingData() {
        return this.selectionData || {
          totalPropertiesSelected: 1,
          totalUnitsSelected: 4,
          selectedUnits: [
            { unitId: 'u1', unitName: 'Unit 1', unitType: '2 Bedroom', unitPrice: 24000000, rentDate: '05 May, 2026' },
            { unitId: 'u2', unitName: 'Unit 2', unitType: '2 Bedroom', unitPrice: 24000000, rentDate: '30 May, 2026' }
          ]
        };
      },
      // Iterates dynamically over the inner structured properties to return aggregated totals
      totalEstimatedRevenue() {
        return this.incomingData.selectedUnits.reduce((acc, current) => acc + current.unitPrice, 0);
      }
    },
    methods: {
      formatPrice(value) {
        return value.toLocaleString();
      }
    }
  };
  </script>



<style lang="scss" scoped>
// Design Tokens Matrix
$color-bg-base: #f9fbfd;
$color-brand-blue: #004bff;
$color-text-main: #0c121d;
$color-text-muted: #535d6e;
$color-white: #ffffff;
$color-border-card: #f0f3f8;

.page-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;
  background-color: $color-bg-base;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding: 40px 16px;
  box-sizing: border-box;

  .card-wrapper {
    width: 100%;
    max-width: 480px;
    display: flex;
    flex-direction: column;
    background: #f8faff;
    box-shadow: 
      0 0 0 1px rgba(0, 0, 0, 0.05), 
      0 4px 12px rgba(0, 0, 0, 0.05), 
      0 12px 24px rgba(0, 0, 0, 0.05);
      padding: 15px;
      border-radius: 6px;
  }

  // 1. Top Decorative Shield Badge Layout 
  .header-icon-container {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;

    .badge-glow-ring {
      width: 64px;
      height: 64px;
      background-color: #eef2ff;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      .svg-shield {
        width: 26px;
        height: 26px;
        fill: none;
        stroke: $color-brand-blue;
        stroke-width: 2.2;
        stroke-linecap: round;
        stroke-linejoin: round;

        .check-path {
          stroke: $color-brand-blue;
          stroke-width: 2;
        }
      }
    }
  }

  // 2. Central Section Typography Header
  .summary-header {
    text-align: center;
    margin-bottom: 32px;
    padding: 0 8px;

    .main-title {
      font-size: 26px;
      font-weight: 700;
      color: $color-text-main;
      margin: 0 0 12px 0;
      letter-spacing: -0.5px;
    }

    .subtitle {
      font-size: 15px;
      line-height: 1.5;
      color: $color-text-muted;
      margin: 0;
    }
  }

  // 3. Core High-Level Metallic Blue Summary Metric Banner 
  .activation-banner {
    position: relative;
    background-color: $color-brand-blue;
    border-radius: 20px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    box-shadow: 0 12px 28px rgba(0, 75, 255, 0.16);
    overflow: hidden;
    margin-bottom: 32px;
    box-sizing: border-box;

    .meta-label {
      font-size: 11px;
      font-weight: 700;
      color: rgba($color-white, 0.65);
      letter-spacing: 0.8px;
      display: block;
      margin-bottom: 6px;
    }

    .banner-top-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      .banner-highlight-title {
        font-size: 22px;
        font-weight: 700;
        color: $color-white;
        margin: 0;
        letter-spacing: -0.2px;
      }

      .banner-wireframe-shield {
        width: 22px;
        height: 22px;
        fill: none;
        stroke: rgba($color-white, 0.5);
        stroke-width: 1.8;
      }
    }

    .banner-bottom-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;

      .metric-block {
        display: flex;
        flex-direction: column;

        &.align-right {
          text-align: right;
        }

        .value-text {
          font-size: 16px;
          font-weight: 600;
          color: $color-white;

          &.price-text {
            font-size: 20px;
            font-weight: 700;
          }
        }
      }
    }
  }

  // 4. Inner Segment Item Streams
  .details-section {
    width: 100%;

    .section-label {
      font-size: 17px;
      font-weight: 700;
      color: $color-text-main;
      margin: 0 0 16px 0;
      padding-left: 2px;
    }

    .units-timeline-stack {
      display: flex;
      flex-direction: column;

      .unit-card-wrapper {
        display: flex;
        flex-direction: column;
      }

      // Individual Node Card Layout Block Base
      .unit-summary-card {
        background-color: $color-white;
        border: 1.5px solid $color-border-card;
        border-radius: 18px;
        padding: 20px;
        display: flex;
        align-items: center;
        gap: 16px;
        box-shadow: 0 4px 14px rgba(0, 40, 110, 0.015);
        box-sizing: border-box;
        margin-bottom: 20px;

        .icon-avatar-box {
          width: 44px;
          height: 44px;
          background-color: #eef2ff;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 44px;

          .svg-building {
            width: 20px;
            height: 20px;
            fill: #0c1633;
          }
        }

        .unit-text-metrics {
          display: flex;
          flex-direction: column;

          .unit-title {
            font-size: 16px;
            font-weight: 700;
            color: $color-text-main;
            margin: 0 0 4px 0;
          }

          .unit-date-label {
            font-size: 14px;
            color: #8993a4;
            margin: 0 0 12px 0;
            font-weight: 500;
          }

          .unit-card-price {
            font-size: 15px;
            font-weight: 700;
            color: $color-brand-blue;
          }
        }
      }
    }
  }
}

// Mobile Responsive Interface Configurations
@media (max-width: 400px) {
  .page-container {
    padding: 24px 12px;

    .summary-header {
      margin-bottom: 24px;
      .main-title { font-size: 22px; }
      .subtitle { font-size: 13.5px; }
    }

    .activation-banner {
      padding: 18px;
      gap: 24px;
      border-radius: 16px;
      margin-bottom: 24px;

      .banner-top-row .banner-highlight-title { font-size: 19px; }
      .banner-bottom-row .metric-block .value-text {
        font-size: 14px;
        &.price-text { font-size: 17px; }
      }
    }

    .details-section {
      .section-label { font-size: 15.5px; }
      .units-timeline-stack .unit-summary-card {
        padding: 16px;
        gap: 12px;
        border-radius: 14px;

        .icon-avatar-box {
          width: 38px;
          height: 38px;
          min-width: 38px;
          border-radius: 10px;
          .svg-building { width: 18px; height: 18px; }
        }

        .unit-text-metrics {
          .unit-title { font-size: 14.5px; }
          .unit-date-label { font-size: 13px; margin-bottom: 8px; }
          .unit-card-price { font-size: 14px; }
        }
      }
    }
  }
}
</style>

  